"use client";

import * as React from "react";
import { type DateRange } from "react-day-picker";

import { ComparisonCalendar } from "@tracify/ui/comparison-calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@tracify/ui/popover";
import { Button } from "@tracify/ui/button";
import { useTimeRangeLabel } from "@lib/hooks/useTimeRangeLabel";
import { useIsMobile } from "@tracify/ui";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectVariants,
} from "@tracify/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import { PREVIOUS_DATE_RANGE_SELECT_OPTIONS } from "@/constants/constants";
import { useDateRangeState } from "@/lib/hooks/url-state";
import { getPreviousDateRange } from "@/services/DateRangeServices";
import { useGranularity } from "@lib/hooks/use-granularity";
import {
  isPresetCompatible,
  getPresetDisabledReason,
  getAlignedComparisonRange,
} from "@lib/util-functions/comparisonBuckets";
import dayjs from "dayjs";
import { cn } from "@lib/utils";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import {
  getMostAheadTimezone,
  tzDayjs,
  getTodayMaxDate,
} from "@lib/util-functions/dates";

type Props = {
  maxDate?: Date;
  minDate?: Date;
};

export function dateRangeModifiers(ranges: DateRange[]) {
  const [currentRange, comparisonRange] = ranges;

  if (!currentRange?.from || !currentRange?.to) {
    return {};
  }

  const modifiers: Record<string, Date[] | { after: Date; before: Date }[]> =
    {};

  // Current range dates
  const currentStart = new Date(currentRange.from);
  const currentEnd = new Date(currentRange.to);

  // If no comparison range, just show current range
  if (!comparisonRange?.from || !comparisonRange?.to) {
    modifiers.current_range_start = [currentStart];
    modifiers.current_range_end = [currentEnd];
    if (currentStart.getTime() !== currentEnd.getTime()) {
      modifiers.current_range_middle = [
        { after: currentStart, before: currentEnd },
      ];
    }
    return modifiers;
  }

  const comparisonStart = new Date(comparisonRange.from);
  const comparisonEnd = new Date(comparisonRange.to);

  // Calculate overlapping dates
  const overlapStart = new Date(
    Math.max(currentStart.getTime(), comparisonStart.getTime())
  );
  const overlapEnd = new Date(
    Math.min(currentEnd.getTime(), comparisonEnd.getTime())
  );
  const hasOverlap = overlapStart <= overlapEnd;

  if (hasOverlap) {
    // 1. First, define the overlap area (highest priority)
    modifiers.overlap_start = [overlapStart];
    modifiers.overlap_end = [overlapEnd];
    if (overlapStart.getTime() !== overlapEnd.getTime()) {
      modifiers.overlap_middle = [{ after: overlapStart, before: overlapEnd }];
    }

    // 2. Current range sections (only parts NOT in overlap)
    // Current range before overlap
    if (currentStart < overlapStart) {
      const dayBeforeOverlap = new Date(
        overlapStart.getTime() - 24 * 60 * 60 * 1000
      );

      // Only the actual start gets start styling
      modifiers.current_range_start = [currentStart];

      // If this segment ends at the actual range start, it gets end styling
      // Otherwise, it's just middle styling up to the overlap
      if (currentStart.getTime() === dayBeforeOverlap.getTime()) {
        // Single day before overlap - already handled by start
      } else {
        modifiers.current_range_middle = [
          { after: currentStart, before: dayBeforeOverlap },
        ];
        // The day before overlap gets middle styling (not end styling)
        modifiers.current_range_middle_before_overlap = [dayBeforeOverlap];
      }
    }

    // Current range after overlap
    if (currentEnd > overlapEnd) {
      const dayAfterOverlap = new Date(
        overlapEnd.getTime() + 24 * 60 * 60 * 1000
      );

      // Only the actual end gets end styling
      modifiers.current_range_end = [currentEnd];

      // If this segment starts at the actual range end, it gets start styling
      // Otherwise, it's just middle styling from the overlap
      if (dayAfterOverlap.getTime() === currentEnd.getTime()) {
        // Single day after overlap - already handled by end
      } else {
        modifiers.current_range_middle_after_overlap = [dayAfterOverlap];
        modifiers.current_range_middle_after = [
          { after: dayAfterOverlap, before: currentEnd },
        ];
      }
    }

    // 3. Comparison range sections (only parts NOT in overlap)
    // Comparison range before overlap
    if (comparisonStart < overlapStart) {
      const dayBeforeOverlap = new Date(
        overlapStart.getTime() - 24 * 60 * 60 * 1000
      );

      // Only the actual start gets start styling
      modifiers.comparison_range_start = [comparisonStart];

      // If this segment ends at the actual range start, it gets end styling
      // Otherwise, it's just middle styling up to the overlap
      if (comparisonStart.getTime() === dayBeforeOverlap.getTime()) {
        // Single day before overlap - already handled by start
      } else {
        modifiers.comparison_range_middle = [
          { after: comparisonStart, before: dayBeforeOverlap },
        ];
        // The day before overlap gets middle styling (not end styling)
        modifiers.comparison_range_middle_before_overlap = [dayBeforeOverlap];
      }
    }

    // Comparison range after overlap
    if (comparisonEnd > overlapEnd) {
      const dayAfterOverlap = new Date(
        overlapEnd.getTime() + 24 * 60 * 60 * 1000
      );

      // Only the actual end gets end styling
      modifiers.comparison_range_end = [comparisonEnd];

      // If this segment starts at the actual range end, it gets start styling
      // Otherwise, it's just middle styling from the overlap
      if (dayAfterOverlap.getTime() === comparisonEnd.getTime()) {
        // Single day after overlap - already handled by end
      } else {
        modifiers.comparison_range_middle_after_overlap = [dayAfterOverlap];
        modifiers.comparison_range_middle_after = [
          { after: dayAfterOverlap, before: comparisonEnd },
        ];
      }
    }
  } else {
    // No overlap - show both ranges separately
    modifiers.current_range_start = [currentStart];
    modifiers.current_range_end = [currentEnd];
    if (currentStart.getTime() !== currentEnd.getTime()) {
      modifiers.current_range_middle = [
        { after: currentStart, before: currentEnd },
      ];
    }

    modifiers.comparison_range_start = [comparisonStart];
    modifiers.comparison_range_end = [comparisonEnd];
    if (comparisonStart.getTime() !== comparisonEnd.getTime()) {
      modifiers.comparison_range_middle = [
        { after: comparisonStart, before: comparisonEnd },
      ];
    }
  }

  return modifiers;
}

export function ComparisonDatePicker({ maxDate: maxDateProp, minDate }: Props) {
  const [open, setOpen] = React.useState(false);

  // URL state from nuqs hooks (merged date range + comparison state)
  const {
    startTime,
    endTime,
    previousStartTime,
    previousEndTime,
    comparePeriod,
    compared,
    setAllDates,
  } = useDateRangeState();
  const { comparedLabel } = useTimeRangeLabel();
  const isMobile = useIsMobile();
  const { selectedStores } = useSelectedStores();

  // Get the most ahead timezone for "today" calculations
  const timezone = React.useMemo(
    () => getMostAheadTimezone(selectedStores),
    [selectedStores]
  );

  // Use provided maxDate (already timezone-aware from dashboard) or fallback to store's "today"
  const maxDate = React.useMemo(
    () => maxDateProp ?? getTodayMaxDate(timezone),
    [maxDateProp, timezone]
  );

  // Get effective granularity for bucket alignment
  const { effectiveGranularity } = useGranularity();
  const [localComparePeriod, setLocalComparePeriod] = React.useState<
    typeof comparePeriod | undefined
  >(comparePeriod);

  // Create browser-local dates for correct Calendar display
  const [localDateRange, setLocalDateRange] = React.useState<
    DateRange | undefined
  >(
    previousStartTime && previousEndTime && comparePeriod !== "no_comparison"
      ? {
          from: new Date(previousStartTime + "T00:00:00"),
          to: new Date(previousEndTime + "T23:59:59"),
        }
      : undefined
  );

  // Ref to track if we're closing with an applied change (to skip reset on close)
  const isClosingWithApplyRef = React.useRef(false);

  // When we close after an apply/preset, defer URL writes until after close to avoid UI flicker.
  const pendingUrlUpdateRef = React.useRef<
    Parameters<typeof setAllDates>[0] | null
  >(null);

  // Prevent double-fire: pointerdown + click.
  const ignoreNextClickRef = React.useRef(false);

  const closeAndCommit = React.useCallback(
    async (updateParams: Parameters<typeof setAllDates>[0]) => {
      pendingUrlUpdateRef.current = updateParams;
      isClosingWithApplyRef.current = true;

      try {
        await setAllDates(updateParams);
      } finally {
        setOpen(false);
        pendingUrlUpdateRef.current = null;
        isClosingWithApplyRef.current = false;
      }
    },
    [setAllDates]
  );

  // Reset comparison if preset becomes incompatible after granularity change
  React.useEffect(() => {
    if (
      comparePeriod !== "no_comparison" &&
      comparePeriod !== "custom" &&
      !isPresetCompatible(comparePeriod, effectiveGranularity)
    ) {
      // The current preset is no longer compatible with the granularity
      // Reset to no comparison (atomic update)
      setAllDates({
        comparePeriod: "no_comparison",
        compare: false,
      });
    }
  }, [effectiveGranularity, comparePeriod, setAllDates]);

  // Current range (read-only, always displayed)
  // Create browser-local dates for correct Calendar display
  const currentRange: DateRange = React.useMemo(
    () => ({
      from: new Date(startTime + "T00:00:00"),
      to: new Date(endTime + "T23:59:59"),
    }),
    [startTime, endTime]
  );

  // All ranges to display (current + comparison if exists)
  const ranges = React.useMemo(() => {
    const allRanges: DateRange[] = [currentRange];
    if (localDateRange?.from && localDateRange?.to) {
      allRanges.push(localDateRange);
    }
    return allRanges;
  }, [currentRange, localDateRange]);
  // Debug the modifiers
  const modifiers = React.useMemo(() => {
    const result = dateRangeModifiers(ranges);
    return result;
  }, [ranges]);

  const handlePresetSelect = (range: typeof comparePeriod) => {
    if (range === "custom") {
      setLocalComparePeriod(range);
      // For custom, we'll wait for user to select a start date
      setLocalDateRange(undefined);
      return;
    }

    // Mark that we're closing with an apply
    isClosingWithApplyRef.current = true;

    if (range === "no_comparison") {
      setLocalDateRange(undefined);
      setLocalComparePeriod(undefined);

      closeAndCommit({
        compare: false,
        comparePeriod: range,
      });
      return;
    } else {
      // Pass granularity for bucket alignment on weekly/monthly
      // Compute comparison based on date-only strings to avoid timezone shifts.
      const dateRange = getPreviousDateRange(
        range,
        { startDate: startTime, endDate: endTime },
        effectiveGranularity
      );

      const startDateStr = dayjs(dateRange.startDate).format("YYYY-MM-DD");
      const endDateStr = dayjs(dateRange.endDate).format("YYYY-MM-DD");

      // Create browser-local dates for correct Calendar display
      const newComparisonRange = {
        from: new Date(startDateStr + "T00:00:00"),
        to: new Date(endDateStr + "T23:59:59"),
      };

      // Update local state immediately for visual feedback
      setLocalDateRange(newComparisonRange);

      closeAndCommit({
        compare: true,
        comparePeriod: range,
        previousStartTime: startDateStr,
        previousEndTime: endDateStr,
      });
      return;
    }

    setOpen(false);
  };

  const handleCalendarSelect = (dateRange: DateRange | undefined) => {
    setLocalComparePeriod("custom");

    // For custom selection: use getAlignedComparisonRange to ensure bucket
    // count matches for weekly/monthly granularity, while preserving day offsets.
    const calculateCustomRange = (selectedDate: Date) => {
      // Use browser-local dates for calculation
      const currentStart = new Date(startTime + "T00:00:00");
      const currentEnd = new Date(endTime + "T23:59:59");

      // Use the alignment function which handles bucket count matching
      const alignedRange = getAlignedComparisonRange(
        currentStart,
        currentEnd,
        selectedDate,
        effectiveGranularity
      );

      // Cap at maxDate if necessary
      const finalEnd =
        maxDate && dayjs(alignedRange.endDate).isAfter(maxDate)
          ? maxDate
          : alignedRange.endDate;

      return { from: alignedRange.startDate, to: finalEnd };
    };

    if (dateRange instanceof Date) {
      setLocalDateRange(calculateCustomRange(dateRange));
    } else if (dateRange) {
      const changedDate = dayjs(dateRange.from).isSame(
        localDateRange?.from,
        "day"
      )
        ? dateRange.to
        : dateRange.from;

      if (changedDate) {
        setLocalDateRange(calculateCustomRange(changedDate));
      }
    } else {
      setLocalDateRange(undefined);
    }
  };

  const handleApply = () => {
    // Mark that we're closing with an apply
    isClosingWithApplyRef.current = true;

    if (!localDateRange?.from || !localDateRange?.to) {
      setLocalDateRange(undefined);

      closeAndCommit({
        compare: false,
        comparePeriod: "no_comparison",
      });
      return;
    }

    closeAndCommit({
      compare: true,
      comparePeriod: localComparePeriod ?? "custom",
      previousStartTime: dayjs(localDateRange.from).format("YYYY-MM-DD"),
      previousEndTime: dayjs(localDateRange.to).format("YYYY-MM-DD"),
    });
  };

  const onClose = () => {
    setOpen(false);

    // If we're not closing with an apply, reset to original values
    if (isClosingWithApplyRef.current) {
      return;
    }

    setLocalComparePeriod(comparePeriod);
    if (
      previousStartTime &&
      previousEndTime &&
      comparePeriod !== "no_comparison"
    ) {
      // Create browser-local dates for correct Calendar display
      setLocalDateRange({
        from: new Date(previousStartTime + "T00:00:00"),
        to: new Date(previousEndTime + "T23:59:59"),
      });
    } else {
      setLocalDateRange(undefined);
    }
  };

  const onOpen = () => {
    // Ignore stray reopen events while mid-close.
    if (isClosingWithApplyRef.current || pendingUrlUpdateRef.current) {
      return;
    }

    // Reset the apply flag when opening
    isClosingWithApplyRef.current = false;
    pendingUrlUpdateRef.current = null;
    setOpen(true);
    setLocalComparePeriod(comparePeriod);
    if (comparePeriod !== "no_comparison") {
      // Create browser-local dates for correct Calendar display
      setLocalDateRange({
        from: new Date(previousStartTime + "T00:00:00"),
        to: new Date(previousEndTime + "T23:59:59"),
      });
    }
  };
  // Show Apply button only when there's a pending change
  const showApplyButton = localComparePeriod === "custom";

  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        } else {
          onOpen();
        }
      }}
    >
      <PopoverTrigger className={selectVariants()}>
        <span className="">{comparedLabel}</span>
        <ChevronUpDownIcon className="size-5" />
      </PopoverTrigger>
      <PopoverContent
        className="w-fit p-0"
        align="end"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Mobile Select */}
          <div className="lg:hidden p-3 pb-2">
            <Select
              value={localComparePeriod || "no_comparison"}
              onValueChange={(value) =>
                handlePresetSelect(value as typeof comparePeriod)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select comparison period" />
              </SelectTrigger>
              <SelectContent>
                {PREVIOUS_DATE_RANGE_SELECT_OPTIONS.map((option) => {
                  const isDisabled = !isPresetCompatible(
                    option.value,
                    effectiveGranularity
                  );

                  return (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      id={"date-preset-select-" + option.value}
                      disabled={isDisabled}
                    >
                      {option.label}
                      {isDisabled && " (unavailable)"}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          {/* Desktop Button List */}
          <div className="hidden lg:flex w-48 flex-col h-full py-3">
            {PREVIOUS_DATE_RANGE_SELECT_OPTIONS.map((option) => {
              const isDisabled = !isPresetCompatible(
                option.value,
                effectiveGranularity
              );
              const disabledReason = getPresetDisabledReason(
                option.value,
                effectiveGranularity
              );

              return (
                <Tooltip key={option.value} delayDuration={300}>
                  <TooltipTrigger asChild>
                    <button
                      id={"date-preset-button-" + option.value}
                      data-preset={option.value}
                      disabled={isDisabled}
                      onPointerDown={(e) => {
                        ignoreNextClickRef.current = true;
                        e.preventDefault();
                        e.stopPropagation();
                        !isDisabled && handlePresetSelect(option.value);
                      }}
                      onClick={(e) => {
                        if (ignoreNextClickRef.current) {
                          ignoreNextClickRef.current = false;
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        e.preventDefault();
                        e.stopPropagation();
                        !isDisabled && handlePresetSelect(option.value);
                      }}
                      className={cn(
                        "date-preset-button h-9 mb-1 flex border-l-4 items-center border-box transition-colors duration-200",
                        isDisabled
                          ? "opacity-50 cursor-not-allowed text-muted-foreground border-transparent"
                          : "cursor-pointer hover:bg-muted hover:border-primary",
                        localComparePeriod === option.value && !isDisabled
                          ? "text-primary bg-primary/10 border-primary"
                          : !isDisabled && "text-foreground border-transparent"
                      )}
                    >
                      <p className="ml-4">{option.label}</p>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{isDisabled ? disabledReason : option.description}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          <div className="flex flex-col items-start gap-3 p-2 lg:p-3">
            {}
            <ComparisonCalendar
              {...({
                mode: comparePeriod === "custom" ? "single" : "range",
                defaultMonth: isMobile
                  ? localDateRange?.to || currentRange.to
                  : localDateRange?.from || currentRange.from,
                selected: localDateRange,
                modifiers: modifiers,
                weekStartsOn: 1,
                onSelect: handleCalendarSelect,
                numberOfMonths: isMobile ? 1 : 2,
                showOutsideDays: false,
                disabled: { after: maxDate, before: minDate },
                className: "border-none p-0",
                classNames: {
                  today:
                    maxDate && dayjs(maxDate).isBefore(tzDayjs(timezone), "day")
                      ? "" // Override today styling when it's disabled
                      : undefined,
                },
                modifiersClassNames: {
                  overlap_start: cn("bg-purple-500/50 rounded-l-none"),
                  overlap_end: cn("bg-primary/50 rounded-r-none"),
                },
              } as any)}
            />
            {showApplyButton && (
              <Button
                size="sm"
                onPointerDown={(e) => {
                  ignoreNextClickRef.current = true;
                  e.preventDefault();
                  e.stopPropagation();
                  handleApply();
                }}
                onClick={(e) => {
                  if (ignoreNextClickRef.current) {
                    ignoreNextClickRef.current = false;
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  handleApply();
                }}
                id="apply-date-button"
              >
                Apply
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
